import { useCallback, useState } from 'react';

import type {
  OnFarmImplantQueueEditableField,
  OnFarmImplantQueueFieldValue,
} from '@/services/on-farm-implant';

export type QueueEditHistory = Map<
  string,
  Map<OnFarmImplantQueueEditableField, OnFarmImplantQueueFieldValue>
>;

export type QueueEditHistoryEntry = {
  rowId: string;
  field: OnFarmImplantQueueEditableField;
  previous: OnFarmImplantQueueFieldValue;
};

export const useQueueEditHistory = () => {
  const [history, setHistory] = useState<QueueEditHistory>(() => new Map());

  const record = useCallback((entries: QueueEditHistoryEntry[]) => {
    if (entries.length === 0) return;
    setHistory((current) => {
      const next = new Map(current);
      for (const { rowId, field, previous } of entries) {
        const rowHistory = new Map(next.get(rowId));
        rowHistory.set(field, previous);
        next.set(rowId, rowHistory);
      }
      return next;
    });
  }, []);

  const clear = useCallback(
    (rowId: string, field: OnFarmImplantQueueEditableField) => {
      setHistory((current) => {
        const rowHistory = current.get(rowId);
        if (!rowHistory?.has(field)) return current;
        const next = new Map(current);
        const nextRow = new Map(rowHistory);
        nextRow.delete(field);
        if (nextRow.size === 0) {
          next.delete(rowId);
        } else {
          next.set(rowId, nextRow);
        }
        return next;
      });
    },
    [],
  );

  return { history, record, clear };
};
